// ============================================================
// RIZKA TRAVEL CRM — REDESIGNED LOGIN PAGE
// ============================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) { toast.error('Please enter your email and password.'); return; }
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.firstName}!`);
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgot = (e) => {
        e.preventDefault();
        toast.success('If that email exists, a reset link has been sent.');
        setShowForgot(false);
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(160deg, #0a1628 0%, #102a43 40%, #1a3a5c 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Background decoration */}
            <div style={{
                position: 'absolute', top: '-20%', right: '-10%',
                width: '600px', height: '600px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)',
                pointerEvents: 'none'
            }}/>
            <div style={{
                position: 'absolute', bottom: '-20%', left: '-10%',
                width: '500px', height: '500px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(72,101,129,0.15) 0%, transparent 70%)',
                pointerEvents: 'none'
            }}/>

            <div style={{ width: '100%', maxWidth: '440px', position: 'relative' }}>

                {/* Logo card */}
                <div style={{
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: '16px 16px 0 0',
                    padding: '2rem 2.5rem 1.5rem',
                    textAlign: 'center',
                    borderBottom: '1px solid #e2e8f0'
                }}>
                    <img
                        src="/logos/rizka-travel.png"
                        alt="Rizka Travel"
                        style={{ height: '38px', objectFit: 'contain', maxWidth: '200px', display: 'block', margin: '0 auto 0.75rem' }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                    <div style={{
                        display: 'none', alignItems: 'center', justifyContent: 'center',
                        gap: '0.5rem', marginBottom: '0.75rem'
                    }}>
                        <div style={{
                            background: '#102a43', color: 'white', padding: '0.4rem 0.8rem',
                            borderRadius: '4px', fontWeight: '800', fontSize: '1.1rem', letterSpacing: '0.08em'
                        }}>RIZKA</div>
                        <div style={{
                            background: '#C9A84C', color: 'white', padding: '0.4rem 0.8rem',
                            borderRadius: '4px', fontWeight: '800', fontSize: '1.1rem', letterSpacing: '0.08em'
                        }}>TRAVEL</div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        <span style={{
                            background: '#102a43', color: 'white',
                            padding: '0.2rem 0.6rem', borderRadius: '4px',
                            fontSize: '0.6875rem', fontWeight: '600', letterSpacing: '0.05em'
                        }}>TravelinBusiness</span>
                        <span style={{
                            background: 'transparent', color: '#C9A84C',
                            padding: '0.2rem 0.6rem', borderRadius: '4px',
                            fontSize: '0.6875rem', fontWeight: '600', letterSpacing: '0.05em',
                            border: '1px solid #C9A84C'
                        }}>HighStreet Holidays</span>
                    </div>
                </div>

                {/* Login form */}
                <div style={{
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: '0 0 16px 16px',
                    padding: '2rem 2.5rem 2.5rem',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)'
                }}>
                    {!showForgot ? (
                        <>
                            <h1 style={{ fontSize: '1.375rem', fontWeight: '700', color: '#102a43', margin: '0 0 0.25rem' }}>
                                Sign in to CRM
                            </h1>
                            <p style={{ color: '#627d98', fontSize: '0.875rem', margin: '0 0 1.75rem' }}>
                                Enter your credentials to continue
                            </p>
                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#486581', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Email address
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="you@rizkatravel.co.uk"
                                        disabled={loading}
                                        style={{
                                            width: '100%', padding: '0.625rem 0.875rem',
                                            border: '1.5px solid #d9e2ec', borderRadius: '8px',
                                            fontSize: '0.9375rem', color: '#102a43', background: '#f8fafc',
                                            outline: 'none', boxSizing: 'border-box',
                                            transition: 'border-color 0.2s'
                                        }}
                                        onFocus={e => e.target.style.borderColor = '#486581'}
                                        onBlur={e => e.target.style.borderColor = '#d9e2ec'}
                                    />
                                </div>
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#486581', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="••••••••••"
                                        disabled={loading}
                                        style={{
                                            width: '100%', padding: '0.625rem 0.875rem',
                                            border: '1.5px solid #d9e2ec', borderRadius: '8px',
                                            fontSize: '0.9375rem', color: '#102a43', background: '#f8fafc',
                                            outline: 'none', boxSizing: 'border-box',
                                            transition: 'border-color 0.2s'
                                        }}
                                        onFocus={e => e.target.style.borderColor = '#486581'}
                                        onBlur={e => e.target.style.borderColor = '#d9e2ec'}
                                    />
                                </div>
                                <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
                                    <button type="button" onClick={() => setShowForgot(true)}
                                        style={{ background: 'none', border: 'none', color: '#486581', fontSize: '0.8125rem', cursor: 'pointer', padding: 0 }}>
                                        Forgot password?
                                    </button>
                                </div>
                                <button type="submit" disabled={loading} style={{
                                    width: '100%', padding: '0.75rem',
                                    background: loading ? '#829ab1' : '#102a43',
                                    color: 'white', border: 'none', borderRadius: '8px',
                                    fontSize: '0.9375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'background 0.2s', letterSpacing: '0.02em'
                                }}>
                                    {loading ? 'Signing in...' : 'Sign in'}
                                </button>
                            </form>
                        </>
                    ) : (
                        <>
                            <h1 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#102a43', margin: '0 0 0.25rem' }}>
                                Reset password
                            </h1>
                            <p style={{ color: '#627d98', fontSize: '0.875rem', margin: '0 0 1.5rem' }}>
                                Enter your email and we'll send a reset link
                            </p>
                            <form onSubmit={handleForgot}>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#486581', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Email address
                                    </label>
                                    <input
                                        type="email"
                                        value={forgotEmail}
                                        onChange={e => setForgotEmail(e.target.value)}
                                        placeholder="you@rizkatravel.co.uk"
                                        style={{
                                            width: '100%', padding: '0.625rem 0.875rem',
                                            border: '1.5px solid #d9e2ec', borderRadius: '8px',
                                            fontSize: '0.9375rem', color: '#102a43', background: '#f8fafc',
                                            outline: 'none', boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                <button type="submit" style={{
                                    width: '100%', padding: '0.75rem',
                                    background: '#102a43', color: 'white', border: 'none',
                                    borderRadius: '8px', fontSize: '0.9375rem', fontWeight: '600', cursor: 'pointer',
                                    marginBottom: '0.75rem'
                                }}>
                                    Send reset link
                                </button>
                                <button type="button" onClick={() => setShowForgot(false)} style={{
                                    width: '100%', padding: '0.625rem',
                                    background: 'none', color: '#627d98', border: '1.5px solid #d9e2ec',
                                    borderRadius: '8px', fontSize: '0.875rem', cursor: 'pointer'
                                }}>
                                    Back to sign in
                                </button>
                            </form>
                        </>
                    )}
                </div>

                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.6875rem', marginTop: '1.25rem' }}>
                    Rizka Travel Ltd · Reg 10763351 · ATOL 12118 · Confidential
                </p>
            </div>
        </div>
    );
}
